/*
 * PDP panel redesign (Oct 2026): keeps the add-to-cart button label as "Add to cart · $X".
 * X = (subscription price if a Recharge selling plan is selected, else variant price) × quantity.
 * Step 3: also re-prices the size cards / unit-price line; step 4: drives the sticky bar.
 * Step 5 (Oct 2026 mockups): "Choose how to buy" radio cards — re-prices the Autoship card and its
 * "Save $X" badge per variant and shows "with Autoship" on the size cards. The buttons keep the
 * "Add to cart · $X" label in both modes (a "Start Autoship" label was tried and dropped, 12 Oct 2026).
 *
 * theme.js rewrites the button's innerHTML on every variant change and then dispatches
 * `variant:changed`, so we re-apply the label after that event. Recharge writes the plan id
 * into the form's hidden `selling_plan` input without an event we can rely on, so we also
 * re-check (deferred) after any change/click inside the form.
 */
(function () {
  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var value = (cents / 100).toFixed(2).split('.');
    var withCommas = value[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var withPeriods = value[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    // The store format renders "$ 45.77"; the panel price shows "$45.77", so drop the gap.
    return format.replace(/\$\s+\{\{/, '${{').replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
      switch (key) {
        case 'amount_no_decimals': return withCommas;
        case 'amount_with_comma_separator': return withPeriods + ',' + value[1];
        case 'amount_no_decimals_with_comma_separator': return withPeriods;
        default: return withCommas + '.' + value[1];
      }
    });
  }

  function init(section) {
    var form = section.querySelector('form.product-form');
    var button = form && form.querySelector('[data-pdp-atc]');
    var jsonEl = section.querySelector('[data-product-json]');
    if (!button || !jsonEl) return;

    var product;
    try {
      product = JSON.parse(jsonEl.textContent).product;
    } catch (e) {
      return;
    }

    function currentVariant() {
      var idInput = form.querySelector('[name="id"]');
      var id = idInput ? parseInt(idInput.value, 10) : NaN;
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].id === id) return product.variants[i];
      }
      return null;
    }

    // When our toggle exists it is the source of truth (see syncPurchase); otherwise fall back to
    // whatever a third-party widget wrote into the form.
    var chosenPlanId = null;

    // {qty, unit} per variant from snippets/pdp-variant-unit.liquid, e.g. {qty: 60, unit: 'capsule'}
    var unitsByVariant = {};
    var unitsEl = section.querySelector('[data-pdp-units]');
    if (unitsEl) {
      try {
        unitsByVariant = JSON.parse(unitsEl.textContent);
      } catch (e) {}
    }

    // "$0.76 / capsule", or '' when the variant has no unit basis
    function perUnit(cents, variant) {
      var basis = variant && unitsByVariant[variant.id];
      if (!basis || !basis.qty) return '';
      var per = Math.round(cents / basis.qty);
      return per > 0 ? formatMoney(per) + ' / ' + basis.unit : '';
    }

    function unitPrice(variant) {
      var planInput = form.querySelector('[name="selling_plan"]');
      var planId = purchase
        ? chosenPlanId
        : (planInput && planInput.value ? parseInt(planInput.value, 10) : null);
      if (planId && variant.selling_plan_allocations) {
        for (var i = 0; i < variant.selling_plan_allocations.length; i++) {
          if (variant.selling_plan_allocations[i].selling_plan_id === planId) {
            return variant.selling_plan_allocations[i].price;
          }
        }
      }
      return variant.price;
    }

    // Step 3: size cards show the active price (Autoship price when a plan is chosen, with the
    // one-time price struck through) and its price per serving. Prices hidden for gated products.
    var sizes = section.querySelector('.pdp-sizes:not([data-pdp-gated])');

    function updateCards() {
      if (!sizes) return;

      sizes.querySelectorAll('[data-pdp-size-card]').forEach(function (card) {
        var id = parseInt(card.getAttribute('data-variant-id'), 10);
        var variant = product.variants.filter(function (v) { return v.id === id; })[0];
        if (!variant) return;

        var price = unitPrice(variant);
        var onAutoship = price < variant.price;
        // Autoship: "with Autoship" replaces the struck-through one-time price (step 5 mockups);
        // only a real sale (compare_at above the one-time price) keeps its strike-through.
        var compare = variant.compare_at_price > variant.price ? variant.compare_at_price : 0;
        var priceEl = card.querySelector('[data-pdp-card-price]');
        var compareEl = card.querySelector('[data-pdp-card-compare]');
        var planEl = card.querySelector('[data-pdp-card-plan]');
        var perEl = card.querySelector('[data-pdp-card-per]');

        if (priceEl) priceEl.innerHTML = formatMoney(price);
        if (compareEl) compareEl.innerHTML = compare > price ? formatMoney(compare) : '';
        if (planEl) planEl.hidden = !onAutoship;
        if (perEl) perEl.innerHTML = perUnit(price, variant);
      });
    }

    // Price row (products without size cards). theme.js only renders the one-time price on variant
    // change; show the Autoship price with the one-time price struck through when a plan is chosen.
    var priceList = section.querySelector('.product-form__info-item--price:not(.pdp-price--in-cards) .price-list');

    function updatePriceRow(variant) {
      if (!priceList) return;

      var price = unitPrice(variant);
      var compare = price < variant.price ? variant.price : variant.compare_at_price;
      var labels = window.languages || {};

      if (compare > price) {
        priceList.innerHTML =
          '<span class="price price--highlight"><span class="visually-hidden">' + (labels.productSalePrice || '') + '</span>' + formatMoney(price) + '</span>' +
          '<span class="price price--compare"><span class="visually-hidden">' + (labels.productRegularPrice || '') + '</span>' + formatMoney(compare) + '</span>';
      } else {
        priceList.innerHTML = '<span class="price"><span class="visually-hidden">' + (labels.productSalePrice || '') + '</span>' + formatMoney(price) + '</span>';
      }
    }

    // Price row unit-price line (products without size cards).
    var perUnitRow = section.querySelector('[data-pdp-per-unit]');

    function updatePerUnit(variant) {
      if (!perUnitRow) return;
      var text = perUnit(unitPrice(variant), variant);
      perUnitRow.hidden = !text;
      perUnitRow.innerHTML = text;
    }

    // Step 4: sticky bar. Its button clicks the panel's button so the main form is what's added.
    var sticky = section.querySelector('[data-pdp-sticky]');
    var stickyWrapper = sticky && sticky.closest('animate-sticky');

    function updateSticky(variant, qty) {
      if (!sticky) return;

      var stickyButton = sticky.querySelector('[data-pdp-sticky-atc]');
      var available = variant && variant.available && !button.hasAttribute('disabled');
      stickyButton.disabled = !available;
      stickyButton.innerHTML = available
        ? stickyButton.getAttribute('data-label')
        : ((window.languages && window.languages.productFormSoldOut) || 'Sold out');

      if (!variant) return;

      var price = unitPrice(variant);
      var meta = [];
      // Autoship frequency (20 Oct 2026): "every 30 days" text in the meta line (phones, and
      // single-plan products everywhere) + a "Deliver every" select mirroring the panel's one
      // (tablet/desktop, products with 2+ plans). CSS hides the text when the select is shown.
      // Phones drop the per-unit figure in Autoship mode so the line stays "Autoship · every 30 days"
      // on one line (the panel shows the unit price anyway).
      var phone = window.matchMedia && window.matchMedia('(max-width: 640px)').matches;
      var planSelect = purchase && purchase.querySelector('[data-pdp-plan]');
      var stickyFrequency = sticky.querySelector('[data-pdp-sticky-frequency]');
      var stickyPlan = sticky.querySelector('[data-pdp-sticky-plan]');
      if (perUnit(price, variant) && !(phone && chosenPlanId)) meta.push(perUnit(price, variant));
      if (purchase) meta.push(chosenPlanId ? 'Autoship' : 'One-time');
      var metaHtml = meta.join(' · ');
      if (chosenPlanId && planSelect && planSelect.selectedOptions[0]) {
        metaHtml += '<span class="pdp-sticky__meta-freq"> · every ' + planSelect.selectedOptions[0].text + '</span>';
      }
      if (stickyFrequency) {
        stickyFrequency.hidden = !chosenPlanId;
        sticky.classList.toggle('pdp-sticky--select', !!chosenPlanId);
        if (chosenPlanId && stickyPlan) {
          Array.prototype.forEach.call(stickyPlan.options, function (option) {
            var panelOption = planSelect && planSelect.querySelector('option[value="' + option.value + '"]');
            option.disabled = !panelOption || panelOption.disabled;
          });
          stickyPlan.value = String(chosenPlanId);
        }
      }

      sticky.querySelector('[data-pdp-sticky-price]').innerHTML = formatMoney(price * qty);
      sticky.querySelector('[data-pdp-sticky-meta]').innerHTML = metaHtml;

      var variantEl = sticky.querySelector('[data-pdp-sticky-variant]');
      if (variantEl) variantEl.textContent = variant.title;
    }

    // Keep page content (footer, chat bubble) clear of the bar while it is showing.
    function syncStickyOffset() {
      if (!stickyWrapper) return;

      var bar = stickyWrapper.querySelector('.product-sticky-cart');
      var shown = bar && parseFloat(getComputedStyle(bar).opacity) > 0.5 &&
        getComputedStyle(bar).pointerEvents !== 'none';
      var height = shown ? sticky.offsetHeight : 0;
      document.body.style.paddingBottom = height ? height + 'px' : '';

      // Shopify Inbox bubble (<shopify-chat>, closed shadow DOM) is lifted by CSS using this offset
      var root = document.documentElement;
      root.classList.toggle('pdp-sticky-shown', !!height);
      root.style.setProperty('--pdp-sticky-height', height + 'px');
    }

    if (sticky) {
      sticky.querySelector('[data-pdp-sticky-atc]').addEventListener('click', function () {
        if (!button.disabled) button.click();
      });

      var offsetPending = false;
      var queueOffset = function () {
        if (offsetPending) return;
        offsetPending = true;
        // after animate-sticky's rAF toggle and the 250ms+250ms reveal transition
        setTimeout(function () {
          offsetPending = false;
          syncStickyOffset();
        }, 600);
      };
      window.addEventListener('scroll', queueOffset, { passive: true });
      window.addEventListener('resize', queueOffset);
      window.addEventListener('resize', updateSoon); // phone/desktop meta text differs (Autoship)
      queueOffset();
    }

    function update() {
      var variant = currentVariant();
      var qtyInput = form.querySelector('[name="quantity"]');
      var qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);

      updateCards();
      updateSticky(variant, qty);
      if (variant) {
        updatePriceRow(variant);
        updatePerUnit(variant);
        if (purchase) {
          var planSelect = purchase.querySelector('[data-pdp-plan]');
          updateAutoshipCard(variant, planSelect ? parseInt(planSelect.value, 10) : null);
        }
      }

      if (!variant || !variant.available || button.hasAttribute('disabled')) return;

      var label = (window.languages && window.languages.productFormAddToCart) || 'Add to cart';
      button.innerHTML = label + ' · ' + formatMoney(unitPrice(variant) * qty);
    }

    function updateSoon() {
      setTimeout(update, 50);
    }

    /*
     * Step 2/5: Autoship / One-time cards (snippets/pdp-purchase-options.liquid). Writes the chosen
     * plan id into the form's `selling_plan` input (empty = one-time). Plans the current variant
     * has no allocation for are disabled; if it has none at all, Autoship is disabled too. The
     * Autoship card always shows the current variant's price on the selected frequency plus the
     * saving vs one-time, even while One-time is selected.
     */
    var purchase = section.querySelector('[data-pdp-purchase]');

    // NB: theme.js dispatches `variant:changed` BEFORE it writes the new id into the form's
    // [name="id"] input, so anything that reads currentVariant() on that event sees the previous
    // variant. update() is re-run by updateSoon() 50ms after the click, which is when the
    // Autoship card gets its real price (it used to lag one size click behind, 12 Oct 2026).
    function planPrice(variant, planId) {
      var allocations = (variant && variant.selling_plan_allocations) || [];
      for (var i = 0; i < allocations.length; i++) {
        if (allocations[i].selling_plan_id === planId) return allocations[i].price;
      }
      return variant ? variant.price : 0;
    }

    function updateAutoshipCard(variant, planId) {
      var priceEl = purchase.querySelector('[data-pdp-autoship-price]');
      var saveEl = purchase.querySelector('[data-pdp-autoship-save]');
      if (!variant || !planId) {
        if (saveEl) saveEl.hidden = true;
        return;
      }
      var price = planPrice(variant, planId);
      var saving = variant.price - price;
      if (priceEl) priceEl.innerHTML = formatMoney(price);
      if (saveEl) {
        saveEl.hidden = saving <= 0;
        saveEl.innerHTML = saving > 0 ? 'Save ' + formatMoney(saving) : '';
      }
    }

    function syncPurchase() {
      if (!purchase) return;

      var variant = currentVariant();
      var planSelect = purchase.querySelector('[data-pdp-plan]');
      var frequency = purchase.querySelector('[data-pdp-frequency]');
      var autoshipRadio = purchase.querySelector('[data-pdp-purchase-mode][value="autoship"]');
      var onetimeRadio = purchase.querySelector('[data-pdp-purchase-mode][value="onetime"]');

      var allowed = {};
      var anyAllowed = false;
      ((variant && variant.selling_plan_allocations) || []).forEach(function (allocation) {
        allowed[allocation.selling_plan_id] = true;
        anyAllowed = true;
      });

      Array.prototype.forEach.call(planSelect.options, function (option) {
        option.disabled = !allowed[parseInt(option.value, 10)];
      });
      if (planSelect.selectedOptions[0] && planSelect.selectedOptions[0].disabled) {
        var firstEnabled = Array.prototype.find.call(planSelect.options, function (o) { return !o.disabled; });
        if (firstEnabled) planSelect.value = firstEnabled.value;
      }

      autoshipRadio.disabled = !anyAllowed;
      if (!anyAllowed && autoshipRadio.checked && onetimeRadio) onetimeRadio.checked = true;

      var autoship = autoshipRadio.checked && anyAllowed;
      frequency.hidden = !autoship;
      chosenPlanId = autoship ? parseInt(planSelect.value, 10) : null;
      purchase.classList.toggle('pdp-purchase--autoship', autoship);
      purchase.querySelectorAll('[data-pdp-purchase-mode]').forEach(function (radio) {
        var card = radio.closest('.pdp-purchase__card');
        if (card) card.classList.toggle('pdp-purchase__card--selected', radio.checked);
      });
      updateAutoshipCard(variant, anyAllowed ? parseInt(planSelect.value, 10) : null);

      // Recharge's app embed still auto-injects its (CSS-hidden) widget, which shares/creates a
      // `selling_plan` input and can reset it on variant change. Write every one of them.
      form.querySelectorAll('[name="selling_plan"]').forEach(function (input) {
        input.value = chosenPlanId ? String(chosenPlanId) : '';
      });
    }

    if (purchase) {
      var detailsToggle = purchase.querySelector('[data-pdp-details-toggle]');
      var detailsBody = purchase.querySelector('[data-pdp-details]');
      if (detailsToggle && detailsBody) {
        detailsToggle.addEventListener('click', function () {
          detailsBody.hidden = !detailsBody.hidden;
          detailsToggle.setAttribute('aria-expanded', detailsBody.hidden ? 'false' : 'true');
        });
      }

      purchase.addEventListener('change', function () {
        syncPurchase();
        update();
      });
      section.addEventListener('variant:changed', syncPurchase);

      // Re-assert our plan right before the form is read: capture phase runs before theme.js's
      // delegated add-to-cart click handler and before native submit.
      section.addEventListener('click', function (event) {
        if (event.target.closest('[data-pdp-atc]')) syncPurchase();
      }, true);
      form.addEventListener('submit', syncPurchase, true);

      // Sticky bar's "Deliver every" select drives the panel's select (the source of truth).
      var stickyPlanSelect = sticky && sticky.querySelector('[data-pdp-sticky-plan]');
      if (stickyPlanSelect) {
        stickyPlanSelect.addEventListener('change', function () {
          var planSelect = purchase.querySelector('[data-pdp-plan]');
          if (planSelect) planSelect.value = stickyPlanSelect.value;
          syncPurchase();
          update();
        });
      }

      syncPurchase();
    }

    section.addEventListener('variant:changed', update);
    form.addEventListener('change', updateSoon);
    form.addEventListener('input', updateSoon);
    form.addEventListener('click', updateSoon);
    update();
  }

  function initAll() {
    document.querySelectorAll('[data-section-type="product"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();

/*
 * Description "Learn More" clamp (14 Oct 2026): theme.js clips the description block at a fixed
 * height (theme.css: 320px, 500px from 1025px up) wherever that lands — mid-sentence, through an
 * image. This moves the clip to the bottom of the last whole paragraph / bullet / heading that fits
 * under that height (a block may overshoot it by up to 20% rather than be dropped), by setting
 * --pdp-desc-clamp on the block (component-pdp-panel.css applies it while the block is collapsed). theme.js's open animation ends with an inline max-height: none and
 * its close clears the inline style, so the custom property survives both. If nothing sensible fits
 * (e.g. the first block is a tall image), the theme's fixed clamp stays as the fallback.
 */
(function () {
  var CLAMPED = 'pdp-desc--clamped';
  var NO_ANIM = 'pdp-desc--no-anim';
  var GAP = 8; // space between the last visible block and the Learn More button
  var OVERSHOOT = 1.2; // a block may end up to 20% past the theme's height rather than be dropped
  var MIN_SHARE = 0.25; // but a preview shorter than 25% of that height falls back to the hard clip

  function isHeadingLike(block) {
    if (/^H[1-6]$/.test(block.tagName)) return true;
    var text = block.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return false;
    if (/:$/.test(text)) return true;
    var bold = block.querySelector('strong, b');
    return !!bold && text.length < 90 && bold.textContent.replace(/\s+/g, ' ').trim() === text;
  }

  function isEmpty(block) {
    return !block.textContent.replace(/[\s ]+/g, '') && !block.querySelector('img, video, iframe, table');
  }

  // Flatten the rich text into candidate end points: paragraphs, headings, list items, images…
  function collectBlocks(container, out) {
    Array.prototype.forEach.call(container.children, function (child) {
      if (/^(UL|OL|DIV|SECTION|ARTICLE)$/.test(child.tagName) && child.children.length) {
        collectBlocks(child, out);
      } else {
        out.push(child);
      }
    });
    return out;
  }

  function clamp(box) {
    if (!box || box.getAttribute('aria-expanded') !== 'false') return;
    var toggle = box.querySelector('.expandable-content__toggle');
    var rte = box.querySelector('.rte');
    if (!toggle || !rte) return;

    box.classList.add(NO_ANIM);
    box.classList.remove(CLAMPED);
    var target = parseFloat(getComputedStyle(box).maxHeight); // the theme's fixed clamp
    if (!target) { box.classList.remove(NO_ANIM); return; }

    var budget = target - toggle.offsetHeight - GAP;
    var boxTop = box.getBoundingClientRect().top;
    var blocks = collectBlocks(rte, []);
    var fits = [];
    for (var i = 0; i < blocks.length; i++) {
      var rect = blocks[i].getBoundingClientRect();
      if (!rect.height) continue;
      if (rect.bottom - boxTop > budget * OVERSHOOT) break;
      fits.push({ block: blocks[i], bottom: rect.bottom - boxTop });
    }
    // Never leave a heading or a blank spacer as the last thing before the button.
    while (fits.length && (isHeadingLike(fits[fits.length - 1].block) || isEmpty(fits[fits.length - 1].block))) {
      fits.pop();
    }
    var last = fits[fits.length - 1];
    if (last && last.bottom >= target * MIN_SHARE) {
      box.style.setProperty('--pdp-desc-clamp', Math.ceil(last.bottom + GAP + toggle.offsetHeight) + 'px');
      box.classList.add(CLAMPED);
    } else {
      box.style.removeProperty('--pdp-desc-clamp');
    }
    box.offsetHeight; // apply without the 0.2s transition
    box.classList.remove(NO_ANIM);
  }

  function clampAll() {
    document.querySelectorAll('.product-block-list__item--description .expandable-content[aria-expanded="false"]').forEach(clamp);
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(clampAll, 150);
  });

  // theme.js toggles on a body delegate; this document listener runs after it, so on a close we
  // re-measure in case the viewport changed while the description was open.
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('.expandable-content__toggle');
    if (toggle) setTimeout(function () { clamp(toggle.closest('.expandable-content')); }, 250); // after the close transition
  });

  function start() {
    clampAll();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(clampAll);
    window.addEventListener('load', clampAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', clampAll);
})();
